import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { assetUrl } from '../lib/api';
import { ArrowRight } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import usePageContent from '../hooks/usePageContent';
import { KenStory, Timeline } from '../components/KenStory';

// Resolve chapters: prefer explicit about_chapters, fall back to about_content split.
const resolveChapters = (settings) => {
    if (Array.isArray(settings?.about_chapters) && settings.about_chapters.length > 0) {
        return settings.about_chapters
            .map((c) => ({ text: (c.text || '').trim(), image_url: c.image_url || '' }))
            .filter((c) => c.text);
    }
    const content = (settings?.about_content || '').trim();
    if (!content) return [];
    return content
        .split(/\n\s*\n/)
        .map((t) => t.trim())
        .filter(Boolean)
        .map((text) => ({ text, image_url: '' }));
};

const About = () => {
    const { settings } = useSiteSettings();
    const content = usePageContent();
    const chapters = resolveChapters(settings);
    const stats = settings?.about_stats || [];

    useSEO({
        title: 'About Dry Ice Bulls Cleaning System® — Industrial Cleaning Craft Since 1996',
        description: 'Dry Ice Bulls has served North Carolina manufacturers, food processors, and industrial facilities since 1996 with dry ice blasting, laser cleaning, sandblasting, and pressure washing.',
    });

    // Interleave: chapter 1 → stat 1 → chapter 2 → stat 2 → ...
    const flow = [];
    const usedStats = Math.min(chapters.length, stats.length);
    for (let i = 0; i < chapters.length; i += 1) {
        flow.push({ type: 'chapter', value: chapters[i], idx: i });
        if (i < usedStats) {
            flow.push({ type: 'stat', value: stats[i], idx: i });
        }
    }
    const trailingStats = stats.slice(usedStats);

    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="border-b border-white/10 relative overflow-hidden">
                <div className="container-page pt-32 pb-24 relative">
                    <div className="label-caps text-brand-primary mb-6">/ About</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[112px] leading-[0.9] max-w-5xl">
                        Dry ice cleaning<br />is our <span className="text-brand-primary">craft.</span>
                    </h1>
                    <div className="mt-10 max-w-xl text-white/60 leading-relaxed">
                        A short story about who we are, why we started, and how we work — told in the numbers
                        that matter most.
                    </div>
                </div>
                <div className="pointer-events-none absolute -bottom-8 md:-bottom-14 left-0 right-0 text-center">
                    <div className="font-display font-black tracking-tighter text-[18vw] leading-none text-white/[0.035] select-none whitespace-nowrap">
                        SINCE 1996
                    </div>
                </div>
            </section>

            {/* KEN'S STORY + TIMELINE */}
            <KenStory story={content?.ken_story} />
            <Timeline items={content?.timeline} />

            {/* SCROLL STORY */}
            <div data-testid="about-content">
                {chapters.length === 0 && (
                    <div className="container-page py-24 text-white/40">Loading…</div>
                )}
                {flow.map((block) =>
                    block.type === 'chapter' ? (
                        <ChapterBlock key={`c-${block.idx}`} idx={block.idx} chapter={block.value} />
                    ) : (
                        <StatBlock key={`s-${block.idx}`} idx={block.idx} stat={block.value} />
                    )
                )}
            </div>

            {trailingStats.length > 0 && (
                <section className="border-t border-white/10 bg-[#0C0C0C]">
                    <div className="container-page py-20">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                            {trailingStats.map((s, i) => (
                                <div key={i} className="bg-brand-bg p-8 md:p-10">
                                    <div className="font-display font-black text-4xl md:text-5xl text-brand-primary">{s.value}</div>
                                    <div className="label-caps text-white/60 mt-3">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="border-t border-white/10">
                <div className="container-page py-16 md:py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl">
                            Talk to a specialist.
                        </h3>
                        <p className="text-white/60 mt-2 max-w-xl">
                            On-site quote, equipment recommendation, or financing walkthrough — one call.
                        </p>
                    </div>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-7 py-4 label-caps transition-colors"
                        data-testid="about-cta-contact"
                    >
                        Contact Us <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
};

// Chapter with optional image — magazine spread when image is present, big text otherwise.
const ChapterBlock = ({ idx, chapter }) => {
    const alignRight = idx % 2 === 1;
    const hasImage = !!chapter.image_url;

    return (
        <section
            className="border-t border-white/10"
            data-testid={`about-chapter-${idx}`}
        >
            <div className="container-page py-24 md:py-32">
                {hasImage ? (
                    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${alignRight ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                        <div className="lg:col-span-5">
                            <div className="border border-white/10 overflow-hidden bg-brand-surface">
                                <img
                                    src={assetUrl(chapter.image_url)}
                                    alt={`Chapter ${idx + 1}`}
                                    className="w-full h-auto block"
                                    data-testid={`about-chapter-image-${idx}`}
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="label-caps text-brand-primary mb-6">
                                / Chapter {String(idx + 1).padStart(2, '0')}
                            </div>
                            <p className="text-base md:text-lg font-normal text-white/80 leading-relaxed max-w-2xl whitespace-pre-line">
                                {chapter.text}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 ${alignRight ? 'lg:[&>*:first-child]:col-start-4' : ''}`}>
                        <div className="lg:col-span-9">
                            <div className="label-caps text-brand-primary mb-6">
                                / Chapter {String(idx + 1).padStart(2, '0')}
                            </div>
                            <p className="text-base md:text-lg font-normal text-white/80 leading-relaxed max-w-2xl whitespace-pre-line">
                                {chapter.text}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

const StatBlock = ({ idx, stat }) => {
    const alignRight = idx % 2 === 1;
    return (
        <section className="border-t border-white/10 bg-[#0C0C0C]">
            <div className={`container-page py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-6 ${alignRight ? 'text-right lg:[&>*]:col-start-4' : ''}`}>
                <div className="lg:col-span-9">
                    <div className="label-caps text-white/40 mb-3">By the numbers</div>
                    <div className="font-display font-black tracking-tighter leading-none text-brand-primary text-[clamp(6rem,18vw,14rem)]">
                        {stat.value}
                    </div>
                    <div className="mt-4 label-caps text-white/70 md:text-lg tracking-widest">
                        {stat.label}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
